import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import API from "@/lib/api";
import { Badges } from "@/components/Badges";
import { Avatar } from "@/components/Avatar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

export default function Profile() {
  const { user } = useAuth();
  const { categories, catName } = useApp();
  const [full, setFull] = useState(null);
  const [biz, setBiz] = useState({ legal_name: "", trade_name: "", fiscal_number: "", description: "", hours: "", categories: [], public_contacts: { phone: "", email: "" } });
  const [svc, setSvc] = useState({ name: "", surname_initial: "", age: 18, city: "", bio: "", experiences: "", open_categories: [] });

  useEffect(() => {
    API.get("/me/full").then((r) => {
      setFull(r.data);
      if (r.data.business) setBiz({ ...biz, ...r.data.business });
      if (r.data.service_person) setSvc({ ...svc, ...r.data.service_person });
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const openCats = categories.filter((c) => c.open_to_privates);

  const saveBiz = async () => {
    const payload = { ...biz, categories: biz.categories.length ? biz.categories : [{ cat: "casa", subcats: ["altro"], radius: 50 }], location: { city: biz.city || "" } };
    await API.put("/me/business", payload);
    toast.success("Profilo attività salvato");
    API.get("/me/full").then((r) => setFull(r.data));
  };
  const saveSvc = async () => {
    await API.put("/me/service-person", { ...svc, location: { city: svc.city } });
    toast.success("Profilo persona salvato");
    API.get("/me/full").then((r) => setFull(r.data));
  };
  const toggleBizCat = (slug) => {
    const has = biz.categories.find((c) => c.cat === slug);
    setBiz({ ...biz, categories: has ? biz.categories.filter((c) => c.cat !== slug) : [...biz.categories, { cat: slug, subcats: ["altro"], radius: 50 }] });
  };
  const toggleSvcCat = (slug) => {
    const has = svc.open_categories.includes(slug);
    setSvc({ ...svc, open_categories: has ? svc.open_categories.filter((c) => c !== slug) : [...svc.open_categories, slug] });
  };

  return (
    <Layout>
      <h1 className="font-serif text-2xl font-bold text-[#0F1230] mb-3">Il mio profilo</h1>
      <div className="flex items-center gap-3 mb-5">
        <Avatar src={user?.picture} name={user?.name} size={56} />
        <div className="min-w-0">
          <p className="font-semibold text-[#0F1230] truncate">{user?.name}</p>
          <p className="text-sm text-[#4A4E68] truncate">{user?.email}</p>
        </div>
      </div>

      <Tabs defaultValue="business">
        <TabsList className="bg-[#EDEDF4] rounded-full">
          <TabsTrigger value="business" data-testid="tab-business" className="rounded-full">Attività (P.IVA)</TabsTrigger>
          <TabsTrigger value="person" data-testid="tab-person" className="rounded-full">Persona che offre</TabsTrigger>
        </TabsList>

        <TabsContent value="business">
          <div className="bg-white rounded-[18px] border border-[#DADFEA] p-5 space-y-3 mt-3" data-testid="business-form">
            {full?.business?.badges?.length > 0 && <Badges badges={full.business.badges} />}
            <div><Label>Ragione sociale</Label><Input value={biz.legal_name} onChange={(e) => setBiz({ ...biz, legal_name: e.target.value })} className="mt-1" /></div>
            <div><Label>Nome commerciale</Label><Input data-testid="biz-trade" value={biz.trade_name} onChange={(e) => setBiz({ ...biz, trade_name: e.target.value })} className="mt-1" /></div>
            <div><Label>Partita IVA / Numero fiscale</Label><Input data-testid="biz-vat" value={biz.fiscal_number} onChange={(e) => setBiz({ ...biz, fiscal_number: e.target.value })} className="mt-1" /><p className="text-xs text-[#4A4E68] mt-1">Verrà verificata dall'admin (VIES per UE).</p></div>
            <div><Label>Città</Label><Input value={biz.city || ""} onChange={(e) => setBiz({ ...biz, city: e.target.value })} className="mt-1" /></div>
            <div><Label>Descrizione</Label><Textarea value={biz.description} onChange={(e) => setBiz({ ...biz, description: e.target.value })} className="mt-1" /></div>
            <div><Label>Orari</Label><Input value={biz.hours} onChange={(e) => setBiz({ ...biz, hours: e.target.value })} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Telefono pubblico</Label><Input value={biz.public_contacts?.phone || ""} onChange={(e) => setBiz({ ...biz, public_contacts: { ...biz.public_contacts, phone: e.target.value } })} className="mt-1" /></div>
              <div><Label>Email pubblica</Label><Input value={biz.public_contacts?.email || ""} onChange={(e) => setBiz({ ...biz, public_contacts: { ...biz.public_contacts, email: e.target.value } })} className="mt-1" /></div>
            </div>
            <div>
              <Label>Categorie in cui operi</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {categories.filter((c) => c.slug !== "varie").map((c) => (
                  <button key={c.slug} onClick={() => toggleBizCat(c.slug)} className={`px-3 h-9 rounded-full text-sm border ${biz.categories.find((x) => x.cat === c.slug) ? "bg-[#1F4FE0] text-white border-[#1F4FE0]" : "border-[#DADFEA]"}`}>{catName(c)}</button>
                ))}
              </div>
            </div>
            <Button data-testid="save-business" onClick={saveBiz} className="w-full h-11 bg-[#1F4FE0] text-white rounded-full">Salva profilo attività</Button>
          </div>
        </TabsContent>

        <TabsContent value="person">
          <div className="bg-white rounded-[18px] border border-[#DADFEA] p-5 space-y-3 mt-3" data-testid="person-form">
            {full?.service_person?.badges?.length > 0 && <Badges badges={full.service_person.badges} />}
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Nome</Label><Input data-testid="svc-name" value={svc.name} onChange={(e) => setSvc({ ...svc, name: e.target.value })} className="mt-1" /></div>
              <div><Label>Iniziale cognome</Label><Input value={svc.surname_initial} onChange={(e) => setSvc({ ...svc, surname_initial: e.target.value })} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Età</Label><Input type="number" value={svc.age} onChange={(e) => setSvc({ ...svc, age: parseInt(e.target.value) || 18 })} className="mt-1" /></div>
              <div><Label>Città</Label><Input value={svc.city} onChange={(e) => setSvc({ ...svc, city: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label>Presentazione</Label><Textarea value={svc.bio} onChange={(e) => setSvc({ ...svc, bio: e.target.value })} className="mt-1" /></div>
            <div><Label>Esperienze</Label><Textarea value={svc.experiences} onChange={(e) => setSvc({ ...svc, experiences: e.target.value })} className="mt-1" /></div>
            <div>
              <Label>Categorie aperte ai privati</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {openCats.map((c) => (
                  <button key={c.slug} onClick={() => toggleSvcCat(c.slug)} className={`px-3 h-9 rounded-full text-sm border ${svc.open_categories.includes(c.slug) ? "bg-[#1F4FE0] text-white border-[#1F4FE0]" : "border-[#DADFEA]"}`}>{catName(c)}</button>
                ))}
              </div>
              <p className="text-xs text-[#4A4E68] mt-2">L'identità va verificata dall'admin prima di poter rispondere.</p>
            </div>
            <Button data-testid="save-person" onClick={saveSvc} className="w-full h-11 bg-[#1F4FE0] text-white rounded-full">Salva profilo persona</Button>
          </div>
        </TabsContent>
      </Tabs>
    </Layout>
  );
}
